/**
 * Button hover animation — "bubble expand from cursor"
 *
 * A circle originates exactly where the cursor enters the button and
 * expands to fill it. On mouse-leave the bubble collapses back.
 *
 * For most .btn variants the fill colour is close enough in lightness to the
 * text colour that no text-colour toggle is needed. .product-card__add-btn
 * is the exception: white background + dark text, but a fully opaque black
 * fill — so its label needs to flip to white while the bubble covers it.
 * That's driven straight from here (inline style, `!important`) instead of
 * a CSS :hover rule, because Elementor's own widget CSS loads after ours and
 * can otherwise win the cascade — this is the only thing guaranteed to beat
 * it, and it's inherently perfectly in sync with the fill animation.
 */
export function initButtonAnimations(root = document) {
    const gsap = window.gsap;
    if (!gsap) return;

    const buttons = [ ...root.querySelectorAll('a.btn, button.product-card__add-btn') ];
    const initialize = (btn) => {
        if (btn.dataset.btnAnimInit) return;
        btn.dataset.btnAnimInit = '1';

        const fill = btn.querySelector('.btn__fill');
        if (!fill) return;

        const needsColorFlip = btn.classList.contains('product-card__add-btn');

        btn.addEventListener('mouseenter', (e) => {
            const rect        = btn.getBoundingClientRect();
            const x           = e.clientX - rect.left;
            const y           = e.clientY - rect.top;
            const targetScale = (Math.max(rect.width, rect.height) * 3) / 20;

            gsap.killTweensOf(fill);
            gsap.set(fill, { x: x - 10, y: y - 10, scale: 0 });
            gsap.to(fill, {
                scale: targetScale,
                duration: 0.55,
                ease: 'power2.out',
                overwrite: true,
            });

            if (needsColorFlip) {
                btn.style.setProperty('color', '#fff', 'important');
            }
        });

        btn.addEventListener('mouseleave', () => {
            gsap.killTweensOf(fill);
            gsap.to(fill, {
                scale: 0,
                duration: 0.4,
                ease: 'power2.in',
                overwrite: true,
            });

            if (needsColorFlip) {
                // Remove the override rather than forcing dark back: if the
                // button is mid is-loading/is-success/is-error (green/red
                // background), that state's own !important colour should
                // decide, not a stale "hover just ended" value.
                btn.style.removeProperty('color');
            }
        });
    };

    const immediateButtons = buttons.filter((btn) => !btn.closest('.product-card'));
    immediateButtons.forEach(initialize);

    const productButtons = buttons.filter((btn) => !!btn.closest('.product-card'));
    if (!productButtons.length) return;

    if (!('IntersectionObserver' in window)) {
        productButtons.forEach(initialize);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            initialize(entry.target);
        });
    }, {rootMargin: '500px 0px'});

    productButtons.forEach((button) => observer.observe(button));
}
